import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/useAuthStore";
import { AnalyticsService } from "@/services/AnalyticsService";
import { getCustomersCount } from "@/services/CustomerService";
import type {
  TSalesSumRequest,
  TTransactionCountReportRequest,
  TNewCustomerCountReportRequest,
  TRepeatCustomerCountReportRequest,
  TTransactionReportRequest,
  TCustomerReportRequest
} from "@shoutout-labs/market_buzz_crm_types";

export function useDashboardAnalytics(
  queryParams: TSalesSumRequest &
    TTransactionCountReportRequest &
    TNewCustomerCountReportRequest &
    TRepeatCustomerCountReportRequest,
  isAllTime: boolean = false
) {
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const initCompleted = useAuthStore((state) => state.initCompleted);

  // For "All Time", create chart parameters with a reasonable date range
  // We need to provide dates here so React Query's enabled condition is true
  // The backend will detect "All Time" requests and use the actual data range instead
  const allTimeChartParams = {
    fromDate: "2023-01-01", // Frontend needs this for enabled condition, backend will override
    toDate: new Date().toISOString().split("T")[0], // Frontend needs this for enabled condition, backend will override
    bucketBy: "day"
  };

  const chartQueryParms = isAllTime
    ? allTimeChartParams
    : {
        ...queryParams,
        bucketBy: "day"
      };

  // Total metrics queries
  const { data: totalSalesData, isLoading: isLoadingTotalSales } = useQuery({
    queryKey: ["totalSales"],
    queryFn: () => AnalyticsService.getTotalSalesSumAnalytics(),
    enabled: isLoggedIn() && initCompleted && isAllTime,
    refetchOnWindowFocus: false
  });

  const { data: totalTransactionsData, isLoading: isLoadingTotalTransactions } =
    useQuery({
      queryKey: ["totalTransactions"],
      queryFn: () => AnalyticsService.getTotalTransactionCountAnalytics(),
      enabled: isLoggedIn() && initCompleted && isAllTime,
      refetchOnWindowFocus: false
    });

  const {
    data: totalRepeatCustomersData,
    isLoading: isLoadingTotalRepeatCustomers
  } = useQuery({
    queryKey: ["totalRepeatCustomers"],
    queryFn: () => AnalyticsService.getTotalRepeatCustomerCountAnalytics(),
    enabled: isLoggedIn() && initCompleted && isAllTime,
    refetchOnWindowFocus: false
  });

  const { data: totalNewCustomersData, isLoading: isLoadingTotalNewCustomers } =
    useQuery({
      queryKey: ["totalNewCustomers"],
      queryFn: () => getCustomersCount({}),
      enabled: isLoggedIn() && initCompleted && isAllTime,
      refetchOnWindowFocus: false
    });

  // Period metrics queries
  const { data: salesData, isLoading: isLoadingSales } = useQuery({
    queryKey: ["sales", queryParams],
    queryFn: () => AnalyticsService.getSalesSumAnalytics(queryParams),
    enabled:
      isLoggedIn() && initCompleted && !isAllTime && !!queryParams.fromDate,
    refetchOnWindowFocus: false
  });

  const { data: transactionsData, isLoading: isLoadingTransactions } = useQuery(
    {
      queryKey: ["transactions", queryParams],
      queryFn: () => AnalyticsService.getTransactionCountAnalytics(queryParams),
      enabled:
        isLoggedIn() && initCompleted && !isAllTime && !!queryParams.fromDate,
      refetchOnWindowFocus: false
    }
  );

  const { data: newCustomersData, isLoading: isLoadingNewCustomers } = useQuery(
    {
      queryKey: ["newCustomers", queryParams],
      queryFn: () => AnalyticsService.getNewCustomerCountAnalytics(queryParams),
      enabled:
        isLoggedIn() && initCompleted && !isAllTime && !!queryParams.fromDate,
      refetchOnWindowFocus: false
    }
  );

  const { data: repeatCustomersData, isLoading: isLoadingRepeatCustomers } =
    useQuery({
      queryKey: ["repeatCustomers", queryParams],
      queryFn: () =>
        AnalyticsService.getRepeatCustomerCountAnalytics(queryParams),
      enabled:
        isLoggedIn() && initCompleted && !isAllTime && !!queryParams.fromDate,
      refetchOnWindowFocus: false
    });

  const { data: salesBucket, isLoading: isLoadingSalesBucket } = useQuery({
    queryKey: ["salesBucket", chartQueryParms],
    queryFn: () =>
      AnalyticsService.getSalesDateBucketAnalytics(
        chartQueryParms as TTransactionReportRequest
      ),
    enabled: isLoggedIn() && initCompleted && !!chartQueryParms.fromDate,
    refetchOnWindowFocus: false
  });

  const { data: customersBucket, isLoading: isLoadingCustomersBucket } =
    useQuery({
      queryKey: ["customersBucket", chartQueryParms],
      queryFn: () =>
        AnalyticsService.getCustomersDateBucketAnalytics(
          chartQueryParms as TCustomerReportRequest
        ),
      enabled: isLoggedIn() && initCompleted && !!chartQueryParms.fromDate,
      refetchOnWindowFocus: false
    });

  const {
    data: repeatCustomersBucket,
    isLoading: isLoadingRepeatCustomersBucket
  } = useQuery({
    queryKey: ["repeatCustomersBucket", chartQueryParms],
    queryFn: () =>
      AnalyticsService.getRepeatCustomersDateBucketAnalytics(
        chartQueryParms as TCustomerReportRequest
      ),
    enabled: isLoggedIn() && initCompleted && !!chartQueryParms.fromDate,
    refetchOnWindowFocus: false
  });

  // Transform total customers data to match expected structure
  const transformedTotalNewCustomers = totalNewCustomersData
    ? {
        count: totalNewCustomersData.count,
        compareToCount: 0,
        comparisonPercentage: 0
      }
    : undefined;

  return {
    // Data
    sales: isAllTime ? totalSalesData : salesData,
    transactions: isAllTime ? totalTransactionsData : transactionsData,
    newCustomers: isAllTime ? transformedTotalNewCustomers : newCustomersData,
    repeatCustomers: isAllTime ? totalRepeatCustomersData : repeatCustomersData,
    salesBucket,
    customersBucket,
    repeatCustomersBucket,

    // Loading states
    isLoading: isAllTime
      ? isLoadingTotalSales ||
        isLoadingTotalTransactions ||
        isLoadingTotalNewCustomers ||
        isLoadingTotalRepeatCustomers ||
        isLoadingSalesBucket ||
        isLoadingCustomersBucket ||
        isLoadingRepeatCustomersBucket
      : isLoadingSales ||
        isLoadingTransactions ||
        isLoadingNewCustomers ||
        isLoadingRepeatCustomers ||
        isLoadingSalesBucket ||
        isLoadingCustomersBucket ||
        isLoadingRepeatCustomersBucket
  };
}
